import { useEffect, useMemo } from "react";

const COLORS = ["#0a7e3a", "#22c55e", "#38bdf8", "#00638a", "#facc15", "#f472b6", "#a78bfa"];

// Full-screen "you got free delivery" moment: confetti rain, a delivery van
// driving in, and the amount saved. Closes on the button, the backdrop or Esc.
export default function FreeDeliveryCelebration({ open, city, saved, onClose }) {
  // Fixed per opening so pieces don't jump around on re-render
  const confetti = useMemo(
    () =>
      Array.from({ length: 70 }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        color: COLORS[i % COLORS.length],
        delay: Math.random() * 0.8,
        duration: 2.4 + Math.random() * 1.8,
        drift: (Math.random() - 0.5) * 200,
        spin: 360 + Math.random() * 720,
        round: Math.random() > 0.6,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [open]
  );

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="celebrate-backdrop fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm overflow-hidden"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="free-delivery-title"
    >
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        {confetti.map((piece) => (
          <span
            key={piece.id}
            className="confetti-piece"
            style={{
              left: `${piece.left}%`,
              background: piece.color,
              borderRadius: piece.round ? "50%" : "2px",
              width: piece.round ? 9 : 8,
              height: piece.round ? 9 : 14,
              "--delay": `${piece.delay}s`,
              "--duration": `${piece.duration}s`,
              "--drift": `${piece.drift}px`,
              "--spin": `${piece.spin}deg`,
            }}
          />
        ))}
      </div>

      <div
        className="celebrate-card relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative bg-gradient-to-br from-green-50 via-emerald-50 to-sky-50 pt-8 pb-6 px-6 overflow-hidden">
          <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-green-200/40 blur-2xl" />
          <div className="absolute -bottom-12 -left-8 w-40 h-40 rounded-full bg-sky-200/40 blur-2xl" />

          <div className="relative h-20 flex items-end justify-center">
            <div className="celebrate-truck text-green-700">
              <span className="material-symbols-outlined" style={{ fontSize: 64, fontVariationSettings: "'FILL' 1" }}>
                local_shipping
              </span>
            </div>
          </div>
          <div className="celebrate-road h-[3px] w-full mt-1 rounded" />

          <div className="relative flex justify-center -mt-1">
            <span className="mt-4 inline-flex items-center gap-1 bg-green-700 text-white text-[11px] font-bold uppercase tracking-[0.2em] px-3 py-1 rounded-full shadow">
              <span className="material-symbols-outlined text-sm">celebration</span>
              {city} Exclusive
            </span>
          </div>
        </div>

        <div className="px-6 pt-5 pb-6 text-center">
          <h2 id="free-delivery-title" className="text-3xl font-extrabold tracking-tight">
            <span className="shine-text">Congratulations!</span>
          </h2>
          <p className="mt-2 text-lg font-bold text-slate-800">You've unlocked FREE delivery</p>
          <p className="mt-1 text-sm text-slate-500">
            Every order delivered within {city} ships to your door at no extra cost.
          </p>

          {saved > 0 && (
            <div className="mt-5 flex items-center justify-center gap-3 bg-green-50 border border-green-200 rounded-2xl px-4 py-3">
              <span className="material-symbols-outlined text-green-700" style={{ fontVariationSettings: "'FILL' 1" }}>
                savings
              </span>
              <div className="text-left">
                <p className="text-[11px] font-bold uppercase tracking-wider text-green-800">You save</p>
                <p className="text-xl font-extrabold text-green-700">Rs {saved.toFixed(2)}</p>
                <p className="text-xs text-slate-500">
                  Delivery <span className="line-through">Rs {saved.toFixed(2)}</span>{" "}
                  <span className="font-bold text-green-700">Rs 0</span>
                </p>
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={onClose}
            autoFocus
            className="mt-6 w-full bg-gradient-to-r from-green-700 to-emerald-600 text-white font-bold py-3.5 rounded-xl shadow-lg shadow-green-700/25 hover:scale-[1.02] active:scale-95 transition-transform"
          >
            Awesome, continue checkout
          </button>
        </div>
      </div>
    </div>
  );
}
